import {GoodsGroupRepo} from "../api/goods-group.repo";
import {inject, Injectable} from "@angular/core";
import {DuckDbHelper, sqlDirection, sqlLike, sqlNumber, sqlText} from "./duckdb.helper";
import {LoggingService} from "../../services/logger/logging.service";
import {OrgDto} from "../dto/org.dto";
import {SearchRequest} from "../dto/search/search-table.dto.req";
import {Observable} from "rxjs";
import {WrapperDto} from "../dto/wrapper.dto";
import {SearchGoodsGroupRes} from "../dto/search/search-goods-group.res";
import {isDefined, nonBlank} from "../../libs/utils/epic.utils";
import {map} from "rxjs/operators";
import {SearchFilterResponse} from "../dto/search/search-table.dto.res";
import {GoodsGroupDto} from "../dto/goods-group.dto";

@Injectable({providedIn: 'root'})
export class GoodsGroupDuckDb extends GoodsGroupRepo {

  private readonly duck: DuckDbHelper = inject(DuckDbHelper);
  private readonly log: LoggingService = inject(LoggingService);

  public override getById(_org: OrgDto, id: string): Observable<WrapperDto<GoodsGroupDto>> {
    const select = `
      SELECT g.id          AS id,
             g.name        AS name,
             g.source      AS source,
             g.external_id AS externalId
      FROM goods_group g
      WHERE g.id = ${sqlText(id)}`;
    return this.duck.sql(select).pipe(map((res) => {
      if (DuckDbHelper.hasRes(res)) {
        const goodsGroup = new GoodsGroupDto(res[0]);
        return DuckDbHelper.data('GoodsGroupDto', goodsGroup);
      }
      return DuckDbHelper.data('GoodsGroupDto', new GoodsGroupDto());
    }));
  }

  public override search(_org: OrgDto, request: SearchRequest): Observable<WrapperDto<SearchGoodsGroupRes>> {
    const limit = request.pageSize;
    const offset = request.pageSize * (request.page - 1);
    const sort = this.buildSort(request);
    const query = this.buildQuery(request);
    const filter = this.buildFilter(request);
    const search = `
      SELECT g.id          AS id,
             g.name        AS name,
             g.source      AS source,
             g.external_id AS externalId,
             COUNT(*)         OVER (ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING)               AS total_results
      FROM goods_group g
      WHERE ${isDefined(query) ? query : '1=1'}
        AND ${isDefined(filter) ? filter : '1=1'}
      ORDER BY ${sort} LIMIT ${sqlNumber(limit, 20)}
      OFFSET ${sqlNumber(offset, 0)}`;
    return this.duck.sql(search).pipe(map((res) => {
      const hasRes = isDefined(res) && res.length > 0;
      const firstRow = hasRes ? res[0] : {total_results: 0} as any;
      const result = new SearchGoodsGroupRes({
        items: hasRes ? res : [],
        hits: firstRow.total_results,
        page: request.page,
        pageSize: request.pageSize,
        sortField: request.sortField,
        sortOrder: request.sortOrder,
        filters: request.filters ? request.filters.map((f) => new SearchFilterResponse(f)) : []
      });
      return DuckDbHelper.data('SearchGoodsGroupRes', result);
    }));
  }

  private buildSort(request: SearchRequest) {
    let field;
    if (isDefined(request.sortField)) {
      switch (request.sortField) {
        case 'name':
          field = 'g.name';
          break;
        default:
          this.log.warn('Unhandled sort field "{}"', [request.sortField]);
          field = 'g.name';
      }
    } else {
      field = 'g.name';
    }
    return field + ' ' + sqlDirection(request.sortOrder);
  }

  private buildQuery(request: SearchRequest): string | undefined {
    if (isDefined(request.query) && nonBlank(request.query)) {
      return `(g.name ILIKE ${sqlLike(request.query)})`;
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private buildFilter(_request: SearchRequest): string | undefined {
    return undefined;
  }
}
